import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  TableContainer,
  Flex,
  Badge,
  Select,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  Collapse,
  useDisclosure,
} from '@chakra-ui/react'
import { useRelatorioDeTroca } from '../hooks/RelatorioDeTrocaContext'
import { LinhaTroca } from './LinhaTroca'
import { FiFilter, FiChevronDown, FiChevronUp } from 'react-icons/fi'

export const TabelaTrocas = () => {
  const { trocas = [], isLoading, error, isRefetching, filtros, aplicarFiltros, fornecedores } = useRelatorioDeTroca()
  const [searchParams, setSearchParams] = useSearchParams()

  const temFiltrosURL = searchParams.toString().length > 0
  const { isOpen, onToggle, onOpen } = useDisclosure({ defaultIsOpen: temFiltrosURL })

  const [filtrosLocais, setFiltrosLocais] = useState({
    fornecedor: searchParams.get('fornecedor') || '',
    data_inicial: searchParams.get('data_inicial') || '',
    data_final: searchParams.get('data_final') || '',
  })

  useEffect(() => {
    if (temFiltrosURL) {
      aplicarFiltros(filtrosLocais)
      onOpen()
    }
  }, [])

  const handleFiltroChange = (e) => {
    const { name, value } = e.target
    setFiltrosLocais({
      ...filtrosLocais,
      [name]: value,
    })
  }

  const handleAplicarFiltros = () => {
    const params = new URLSearchParams()

    Object.entries(filtrosLocais).forEach(([key, value]) => {
      if (value) {
        params.set(key, value)
      }
    })

    setSearchParams(params)
    aplicarFiltros(filtrosLocais)
  }

  const handleLimparFiltros = () => {
    const filtrosVazios = {
      fornecedor: '',
      data_inicial: '',
      data_final: '',
    }

    setFiltrosLocais(filtrosVazios)
    setSearchParams({})
    aplicarFiltros(filtrosVazios)
  }

  if (isLoading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" color="blue.500" />
        <Text mt={4}>Carregando relatório de trocas...</Text>
      </Box>
    )
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <Text>Erro ao carregar o relatório de trocas. Por favor, tente novamente.</Text>
      </Alert>
    )
  }

  return (
    <Box>
      <Box mb={6}>
        <Flex justify="space-between" align="center" mb={2}>
          <Button onClick={onToggle} variant="outline" size="sm" leftIcon={isOpen ? <FiChevronUp /> : <FiChevronDown />}>
            {isOpen ? 'Ocultar filtros' : 'Mostrar filtros'}
          </Button>

          <Text fontWeight="medium">Total de registros: {trocas.length}</Text>
        </Flex>

        <Collapse in={isOpen} animateOpacity>
          <Box width="100%" p={4} borderWidth="1px" borderRadius="lg" bg="white" mt={2}>
            <Text fontWeight="bold" mb={4}>
              Filtros
            </Text>

            <HStack spacing={4} flexWrap="wrap">
              <FormControl minW="200px" maxW="300px">
                <FormLabel>Fornecedor</FormLabel>
                <Select name="fornecedor" placeholder="Selecione um fornecedor" value={filtrosLocais.fornecedor} onChange={handleFiltroChange}>
                  {fornecedores.map((fornecedor) => (
                    <option key={fornecedor.codigo_fornecedor} value={fornecedor.codigo_fornecedor}>
                      {fornecedor.nome_fornecedor}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl minW="200px" maxW="250px">
                <FormLabel>Data Inicial</FormLabel>
                <Input name="data_inicial" type="date" value={filtrosLocais.data_inicial} onChange={handleFiltroChange} />
              </FormControl>

              <FormControl minW="200px" maxW="250px">
                <FormLabel>Data Final</FormLabel>
                <Input name="data_final" type="date" value={filtrosLocais.data_final} onChange={handleFiltroChange} />
              </FormControl>

              <Flex alignSelf="flex-end" flexDir="row" alignItems="center" gap={2} spacing={2}>
                <Button leftIcon={<FiFilter />} colorScheme="blue" onClick={handleAplicarFiltros} width="full">
                  Filtrar
                </Button>
                <Button variant="outline" onClick={handleLimparFiltros} width="full">
                  Limpar
                </Button>
              </Flex>
            </HStack>

            {Object.values(filtros).some((f) => f) && (
              <HStack mt={4} spacing={2} flexWrap="wrap">
                <Text fontWeight="medium">Filtros aplicados:</Text>
                {filtros.fornecedor && (
                  <Badge colorScheme="blue" p={1}>
                    Fornecedor: {filtros.fornecedor}
                  </Badge>
                )}
                {filtros.data_inicial && (
                  <Badge colorScheme="green" p={1}>
                    A partir de: {new Date(filtros.data_inicial).toLocaleDateString('pt-BR')}
                  </Badge>
                )}
                {filtros.data_final && (
                  <Badge colorScheme="green" p={1}>
                    Até: {new Date(filtros.data_final).toLocaleDateString('pt-BR')}
                  </Badge>
                )}
              </HStack>
            )}
          </Box>
        </Collapse>
      </Box>

      <Flex justify="flex-end" align="center" mb={4}>
        {isRefetching && (
          <Badge display="flex" alignItems="center" colorScheme="blue" p={2} borderRadius="md">
            <Spinner size="sm" mr={2} />
            <Text fontSize="sm">Atualizando dados...</Text>
          </Badge>
        )}
      </Flex>

      <TableContainer>
        <Table variant="striped" size="md">
          <Thead bg="#378BBF">
            <Tr>
              <Th width="3%" color="white" borderBottom="none" borderRight="none"></Th>
              <Th color="white" borderBottom="none" borderRight="none">
                Data
              </Th>
              <Th color="white" borderBottom="none" borderRight="none" width="150px">
                Fornecedor
              </Th>
              <Th color="white" borderBottom="none" borderRight="none">
                Nota Fiscal
              </Th>
              <Th color="white" borderBottom="none" borderRight="none" width="250px">
                Item
              </Th>
              <Th color="white" borderBottom="none" borderRight="none" textAlign="right">
                Qtd. Pedido
              </Th>
              <Th color="white" borderBottom="none" borderRight="none" textAlign="right">
                Qtd. Troca
              </Th>
              <Th color="white" borderBottom="none" borderRight="none">
                Pedido Innovation
              </Th>
              <Th color="white" borderBottom="none" borderRight="none">
                Pedido SPOT
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {trocas.length > 0 ? (
              trocas.map((troca, index) => <LinhaTroca key={`${troca.numero_pedido}-${index}`} troca={troca} />)
            ) : (
              <Tr>
                <td colSpan={9}>
                  <Text textAlign="center" py={4}>
                    Nenhuma troca encontrada
                  </Text>
                </td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  )
}
